<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Medical Services Management</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Dashboard</a></li>
                    <li class="breadcrumb-item active">Services Management</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.info" class="alert alert-primary alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>

          <!-- Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Total Services</p>
                      <h4 class="mb-0">{{ serviceStats.totalServices }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-list-ul text-primary fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Active Services</p>
                      <h4 class="mb-0">{{ serviceStats.activeServices }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-check-circle text-success fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Service Categories</p>
                      <h4 class="mb-0">{{ serviceStats.totalCategories }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-category text-info fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-3 col-md-6">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-1">
                      <p class="text-muted fw-medium">Covered Services</p>
                      <h4 class="mb-0">{{ serviceStats.coveredServices }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <i class="bx bx-shield-check text-warning fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Services Management Card -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white">
                  <div class="row align-items-center">
                    <div class="col">
                      <h4 class="card-title mb-0">Medical Services Catalog</h4>
                      <p class="text-muted mb-0">Manage medical services, codes, and pricing</p>
                    </div>
                    <div class="col-auto">
                      <div class="d-flex gap-2">
                        <button class="btn btn-outline-primary" @click="openImportModal">
                          <i class="bx bx-upload me-1"></i>
                          Import Services
                        </button>
                        <button class="btn btn-outline-secondary" @click="exportServices">
                          <i class="bx bx-download me-1"></i>
                          Export CSV
                        </button>
                        <div class="dropdown">
                          <button class="btn btn-outline-info dropdown-toggle" type="button" data-bs-toggle="dropdown">
                            <i class="bx bx-filter-alt me-1"></i>
                            {{ getFilterText() }}
                          </button>
                          <ul class="dropdown-menu">
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('all')">All Services</a></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('active')">Active Only</a></li>
                            <li><a class="dropdown-item" href="javascript:void(0);" @click="setFilter('inactive')">Inactive Only</a></li>
                            <li><hr class="dropdown-divider"></li>
                            <li><h6 class="dropdown-header">By Category</h6></li>
                            <li v-for="category in categories" :key="category.category_id">
                              <a class="dropdown-item" href="javascript:void(0);" @click="setFilter('category', category.category_name)">
                                {{ category.category_name }}
                              </a>
                            </li>
                          </ul>
                        </div>
                        <button class="btn btn-success" @click="addNewService">
                          <i class="bx bx-plus-circle me-1"></i>
                          Add Service
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Search Bar -->
                  <div class="row mb-3">
                    <div class="col-md-4">
                      <div class="input-group">
                        <span class="input-group-text">
                          <i class="bx bx-search"></i>
                        </span>
                        <input type="text" class="form-control" v-model="searchQuery"
                               placeholder="Search by name, code, ICD, CPT...">
                      </div>
                    </div>
                    <div class="col-md-3">
                      <select class="form-select" v-model="selectedCategory">
                        <option value="">All Categories</option>
                        <option v-for="cat in categories" :key="cat.category_id" :value="cat.category_name">
                          {{ cat.category_name }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <select class="form-select" v-model="pageSize">
                        <option value="10">10 per page</option>
                        <option value="25">25 per page</option>
                        <option value="50">50 per page</option>
                        <option value="100">100 per page</option>
                      </select>
                    </div>
                  </div>

                  <!-- Loading State -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3">Loading services...</p>
                  </div>

                  <!-- Services Table -->
                  <div v-else class="table-responsive">
                    <table class="table align-middle table-hover border table-striped">
                      <thead class="table-light">
                      <tr>
                        <th scope="col" style="width: 50px;">
                          <div class="form-check">
                            <input class="form-check-input" type="checkbox"
                                   @change="toggleAllServices" v-model="selectAll">
                          </div>
                        </th>
                        <th scope="col">Service Details</th>
                        <th scope="col">Medical Codes</th>
                        <th scope="col">Pricing</th>
                        <th scope="col">Coverage</th>
                        <th scope="col">Status</th>
                        <th scope="col" class="text-center" style="width: 140px;">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(service) in paginatedServices" :key="service.service_id">
                        <td>
                          <div class="form-check">
                            <input class="form-check-input" type="checkbox"
                                   v-model="selectedServices" :value="service.service_id">
                          </div>
                        </td>
                        <td>
                          <div>
                            <h6 class="mb-0 fw-bold">{{ service.service_name }}</h6>
                            <p class="text-muted mb-0 small">
                              <span class="badge bg-soft-primary text-primary me-1">{{ service.service_code }}</span>
                              <span class="text-secondary">{{ service.service_category }}</span>
                            </p>
                            <small class="text-muted" v-if="service.description">
                              {{ truncateText(service.description, 60) }}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex flex-column gap-1">
                            <span v-if="service.icd_code" class="badge bg-soft-info text-info">
                              ICD: {{ service.icd_code }}
                            </span>
                            <span v-if="service.cpt_code" class="badge bg-soft-warning text-warning">
                              CPT: {{ service.cpt_code }}
                            </span>
                            <span v-if="service.snomed_code" class="badge bg-soft-secondary text-secondary">
                              SNOMED: {{ service.snomed_code }}
                            </span>
                            <span v-if="service.local_code" class="badge bg-soft-dark text-dark">
                              Local: {{ service.local_code }}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong class="text-primary">ETB {{ formatCurrency(service.unit_price) }}</strong>
                            <small class="text-muted d-block">Unit Price</small>
                          </div>
                        </td>
                        <td>
                          <div>
                            <span class="badge" :class="getCoverageBadgeClass(service.cbhi_coverage_status)">
                              <i :class="getCoverageIcon(service.cbhi_coverage_status)"></i>
                              {{ formatCoverageStatus(service.cbhi_coverage_status) }}
                            </span>
                            <small class="d-block text-muted" v-if="service.cbhi_copayment_percentage">
                              {{ service.cbhi_copayment_percentage }}% copay
                            </small>
                          </div>
                        </td>
                        <td>
                          <div class="form-check form-switch">
                            <input class="form-check-input" type="checkbox"
                                   :checked="service.is_active == 1"
                                   @change="toggleServiceStatus(service)">
                            <label class="form-check-label">
                              {{ service.is_active == 1 ? 'Active' : 'Inactive' }}
                            </label>
                          </div>
                        </td>
                        <td class="text-center">
                          <div class="btn-group" role="group">
                            <button class="btn btn-outline-primary btn-sm"
                                    @click="viewServiceDetails(service)"
                                    data-bs-toggle="tooltip"
                                    title="View Details">
                              <i class="bx bx-show"></i>
                            </button>
                            <button class="btn btn-outline-secondary btn-sm"
                                    @click="editService(service)"
                                    data-bs-toggle="tooltip"
                                    title="Edit Service">
                              <i class="bx bx-edit"></i>
                            </button>
                            <button class="btn btn-outline-warning btn-sm"
                                    @click="manageCoverage(service)"
                                    data-bs-toggle="tooltip"
                                    title="Coverage Settings">
                              <i class="bx bx-shield"></i>
                            </button>
                            <button class="btn btn-outline-danger btn-sm"
                                    @click="deleteService(service)"
                                    data-bs-toggle="tooltip"
                                    title="Delete Service">
                              <i class="bx bx-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>

                    <!-- No services message -->
                    <div v-if="filteredServices.length === 0" class="text-center py-5">
                      <i class="bx bx-list-ul display-1 text-muted"></i>
                      <p class="text-muted mb-3">{{ getNoServicesMessage() }}</p>
                      <button class="btn btn-primary" @click="addNewService">
                        <i class="bx bx-plus-circle me-2"></i>Create First Service
                      </button>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="row align-items-center mt-3">
                      <div class="col-sm-6">
                        <p class="text-muted mb-0">
                          Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredServices.length }} services
                        </p>
                      </div>
                      <div class="col-sm-6">
                        <nav aria-label="Service pagination">
                          <ul class="pagination justify-content-end mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <a class="page-link" href="javascript:void(0);" @click="previousPage">
                                <i class="bx bx-chevron-left"></i>
                              </a>
                            </li>
                            <li v-for="page in displayedPages" :key="page"
                                class="page-item" :class="{ active: page === currentPage }">
                              <a class="page-link" href="javascript:void(0);" @click="goToPage(page)">
                                {{ page }}
                              </a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                              <a class="page-link" href="javascript:void(0);" @click="nextPage">
                                <i class="bx bx-chevron-right"></i>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>

                  <!-- Bulk Actions -->
                  <div v-if="selectedServices.length > 0" class="mt-3 p-3 bg-light rounded">
                    <div class="d-flex align-items-center justify-content-between">
                      <p class="mb-0">
                        <strong>{{ selectedServices.length }}</strong> service(s) selected
                      </p>
                      <div class="d-flex gap-2">
                        <button class="btn btn-sm btn-outline-success" @click="bulkActivate">
                          <i class="bx bx-check me-1"></i>Activate
                        </button>
                        <button class="btn btn-sm btn-outline-warning" @click="bulkDeactivate">
                          <i class="bx bx-x me-1"></i>Deactivate
                        </button>
                        <button class="btn btn-sm btn-outline-danger" @click="bulkDelete">
                          <i class="bx bx-trash me-1"></i>Delete
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="clearSelection">
                          Clear Selection
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Service Details Modal -->
  <ServiceDetailsModal
      ref="serviceDetailsModal"
      :service="selectedService"
      @edit-service="editService"
      @manage-coverage="manageCoverage"
  />

  <!-- Add/Edit Service Modal -->
  <ServiceFormModal
      ref="serviceFormModal"
      :service="selectedService"
      :is-edit-mode="isEditMode"
      :categories="categories"
      @service-saved="handleServiceSaved"
      @service-error="handleServiceError"
  />

  <!-- Coverage Settings Modal -->
  <ServiceCoverageModal
      ref="serviceCoverageModal"
      :service="selectedService"
      @coverage-updated="handleCoverageUpdated"
  />

  <!-- Import Services Modal -->
  <ServiceImportModal
      ref="serviceImportModal"
      @import-complete="handleImportComplete"
      @import-error="handleImportError"
  />

  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteServiceModal" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-danger">
            <i class="bx bx-trash me-2"></i>Delete Service
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-danger">
            <i class="bx bx-warning me-2"></i>
            <strong>Warning!</strong> This action cannot be undone.
          </div>

          <div class="mb-3">
            <p>Are you sure you want to delete the service:</p>
            <div class="alert alert-light border">
              <strong>{{ serviceToDelete?.service_name }}</strong>
              <br>
              <small class="text-muted">Code: {{ serviceToDelete?.service_code }}</small>
            </div>
          </div>

          <div v-if="serviceToDelete?.claims_count > 0" class="alert alert-warning">
            <i class="bx bx-error-circle me-1"></i>
            This service has been used in <strong>{{ serviceToDelete.claims_count }} claims</strong>.
            Deleting it may affect historical data.
          </div>

          <div class="mb-3">
            <label class="form-label">
              <strong>Type "DELETE" to confirm:</strong>
            </label>
            <input
                type="text"
                class="form-control"
                v-model="deleteConfirmationText"
                placeholder="Type DELETE here"
                :class="{ 'is-invalid': deleteConfirmationText && deleteConfirmationText !== 'DELETE' }">
            <div class="invalid-feedback" v-if="deleteConfirmationText && deleteConfirmationText !== 'DELETE'">
              You must type "DELETE" exactly to confirm
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button"
                  class="btn btn-danger"
                  @click="confirmDeleteService"
                  :disabled="deleteConfirmationText !== 'DELETE'">
            <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bx bx-trash me-1"></i>
            {{ deleting ? 'Deleting...' : 'Delete Service' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ServiceDetailsModal from "@/components/cbhi-plans-services/modals/ServiceDetailsModal.vue";
import ServiceFormModal from "@/components/cbhi-plans-services/modals/ServiceFormModal.vue";
import ServiceCoverageModal from "@/components/cbhi-plans-services/modals/ServiceCoverageModal.vue";
import ServiceImportModal from "@/components/cbhi-plans-services/modals/ServiceImportModal.vue";
import { authUrl, executeJson } from "@/api";

export default {
  name: "ServicesManagement",
  components: {
    NavigationBar,
    ServiceDetailsModal,
    ServiceFormModal,
    ServiceCoverageModal,
    ServiceImportModal
  },
  data() {
    return {
      // Loading state
      loading: false,

      // Services data
      services: [],
      categories: [],
      serviceStats: {
        totalServices: 0,
        activeServices: 0,
        totalCategories: 0,
        coveredServices: 0
      },

      // Filter and search
      currentFilter: 'all',
      selectedCategory: '',
      searchQuery: '',

      // Pagination
      currentPage: 1,
      pageSize: 25,

      // Selection
      selectedServices: [],
      selectAll: false,

      // Modal states
      selectedService: null,
      serviceToDelete: null,
      isEditMode: false,
      deleteConfirmationText: '',
      deleting: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    filteredServices() {
      let filtered = this.services;

      // Apply status filter
      if (this.currentFilter === 'active') {
        filtered = filtered.filter(s => s.is_active == 1);
      } else if (this.currentFilter === 'inactive') {
        filtered = filtered.filter(s => s.is_active == 0);
      }

      // Apply category filter
      if (this.selectedCategory) {
        filtered = filtered.filter(s => s.service_category === this.selectedCategory);
      }

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(s =>
            s.service_name?.toLowerCase().includes(query) ||
            s.service_code?.toLowerCase().includes(query) ||
            s.icd_code?.toLowerCase().includes(query) ||
            s.cpt_code?.toLowerCase().includes(query) ||
            s.snomed_code?.toLowerCase().includes(query) ||
            s.description?.toLowerCase().includes(query)
        );
      }

      return filtered;
    },

    paginatedServices() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredServices.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredServices.length / this.pageSize);
    },

    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },

    endIndex() {
      return Math.min(this.startIndex + this.pageSize, this.filteredServices.length);
    },

    displayedPages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  mounted() {
    this.loadServices();
    this.loadCategories();
    this.loadServiceStats();
  },

  methods: {
    // Load all services
    async loadServices() {
      this.loading = true;

      const data = {
        "function": "getMedicalServices",
        "includeCBHICoverage": "1"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.services = res.data.data.services || [];
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load services');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to load services');
      } finally {
        this.loading = false;
      }
    },

    // Load service categories
    async loadCategories() {
      const data = {
        "function": "getServiceCategories"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.categories = res.data.data.categories || [];
        }
      } catch (e) {
        console.error("Failed to load categories:", e);
      }
    },

    // Load service statistics
    async loadServiceStats() {
      const data = {
        "function": "getServiceStatistics"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.serviceStats = res.data.data.stats || this.serviceStats;
        }
      } catch (e) {
        console.error("Failed to load statistics:", e);
      }
    },

    // Toggle service status
    async toggleServiceStatus(service) {
      const newStatus = service.is_active == 1 ? 0 : 1;
      const action = newStatus === 1 ? 'activate' : 'deactivate';

      const data = {
        "function": "updateServiceStatus",
        "serviceId": service.service_id,
        "status": newStatus
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          service.is_active = newStatus;
          this.showAlert('success', `Service ${action}d successfully`);
          this.loadServiceStats();
        } else {
          this.showAlert('failed', res.data.message || `Failed to ${action} service`);
        }
      } catch (e) {
        this.showAlert('failed', e.message || `Failed to ${action} service`);
      }
    },

    // Import services
    openImportModal() {
      this.$refs.serviceImportModal.openModal();
    },

    // Export services to CSV
    async exportServices() {
      const data = {
        "function": "exportServices",
        "format": "csv"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          // Download the CSV file
          const blob = new Blob([res.data.data.csv_content], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `services_export_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          this.showAlert('success', 'Services exported successfully');
        }
      } catch (e) {
        this.showAlert('failed', 'Failed to export services');
      }
    },

    // Service actions
    addNewService() {
      this.selectedService = null;
      this.isEditMode = false;
      this.$refs.serviceFormModal.openModal();
    },

    editService(service) {
      this.selectedService = { ...service };
      this.isEditMode = true;
      this.$refs.serviceFormModal.openModal();
    },

    viewServiceDetails(service) {
      this.selectedService = service;
      this.$refs.serviceDetailsModal.openModal();
    },

    manageCoverage(service) {
      this.selectedService = service;
      this.$refs.serviceCoverageModal.openModal();
    },

    deleteService(service) {
      this.serviceToDelete = service;
      this.deleteConfirmationText = '';
      this.deleting = false;
      const modal = new bootstrap.Modal(document.getElementById('deleteServiceModal'));
      modal.show();
    },

    async confirmDeleteService() {
      if (this.deleteConfirmationText !== 'DELETE') return;

      this.deleting = true;

      const data = {
        "function": "deleteService",
        "serviceId": this.serviceToDelete.service_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', `Service "${this.serviceToDelete.service_name}" deleted successfully`);
          this.loadServices();
          this.loadServiceStats();

          const modal = bootstrap.Modal.getInstance(document.getElementById('deleteServiceModal'));
          modal.hide();

          this.serviceToDelete = null;
          this.deleteConfirmationText = '';
        } else {
          this.showAlert('failed', res.data.message || 'Failed to delete service');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to delete service');
      } finally {
        this.deleting = false;
      }
    },

    // Bulk actions
    toggleAllServices() {
      if (this.selectAll) {
        this.selectedServices = this.paginatedServices.map(s => s.service_id);
      } else {
        this.selectedServices = [];
      }
    },

    clearSelection() {
      this.selectedServices = [];
      this.selectAll = false;
    },

    async bulkActivate() {
      if (this.selectedServices.length === 0) return;

      const data = {
        "function": "bulkUpdateServiceStatus",
        "serviceIds": this.selectedServices,
        "status": 1
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', `${this.selectedServices.length} services activated successfully`);
          this.loadServices();
          this.loadServiceStats();
          this.clearSelection();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to activate services');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to activate services');
      }
    },

    async bulkDeactivate() {
      if (this.selectedServices.length === 0) return;

      const data = {
        "function": "bulkUpdateServiceStatus",
        "serviceIds": this.selectedServices,
        "status": 0
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', `${this.selectedServices.length} services deactivated successfully`);
          this.loadServices();
          this.loadServiceStats();
          this.clearSelection();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to deactivate services');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to deactivate services');
      }
    },

    async bulkDelete() {
      if (this.selectedServices.length === 0) return;

      if (!confirm(`Are you sure you want to delete ${this.selectedServices.length} services? This action cannot be undone.`)) {
        return;
      }

      const data = {
        "function": "bulkDeleteServices",
        "serviceIds": this.selectedServices
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', `${this.selectedServices.length} services deleted successfully`);
          this.loadServices();
          this.loadServiceStats();
          this.clearSelection();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to delete services');
        }
      } catch (e) {
        this.showAlert('failed', e.message || 'Failed to delete services');
      }
    },

    // Pagination methods
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    goToPage(page) {
      this.currentPage = page;
    },

    // Event handlers
    handleServiceSaved(event) {
      this.showAlert('success', event.message || 'Service saved successfully');
      this.loadServices();
      this.loadServiceStats();
    },

    handleServiceError(event) {
      this.showAlert('failed', event.message || 'Error saving service');
    },

    handleCoverageUpdated(event) {
      this.showAlert('success', event.message || 'Coverage settings updated successfully');
      this.loadServices();
    },

    handleImportComplete(event) {
      this.showAlert('success', event.message || 'Services imported successfully');
      this.loadServices();
      this.loadServiceStats();
    },

    handleImportError(event) {
      this.showAlert('failed', event.message || 'Error importing services');
    },

    // Filter methods
    setFilter(filter, value = null) {
      if (filter === 'category') {
        this.selectedCategory = value;
      } else {
        this.currentFilter = filter;
      }
      this.currentPage = 1;
    },

    getFilterText() {
      if (this.selectedCategory) {
        return this.selectedCategory;
      }
      const filterMap = {
        'all': 'All Services',
        'active': 'Active Only',
        'inactive': 'Inactive Only'
      };
      return filterMap[this.currentFilter] || 'All Services';
    },

    getNoServicesMessage() {
      if (this.searchQuery) {
        return 'No services found matching your search';
      }
      if (this.selectedCategory) {
        return `No services found in ${this.selectedCategory} category`;
      }
      if (this.currentFilter === 'all') {
        return 'No services have been created yet';
      }
      return `No ${this.getFilterText().toLowerCase()} found`;
    },

    // UI Helper Methods
    getCoverageBadgeClass(status) {
      const classMap = {
        'fully_covered': 'bg-success',
        'partially_covered': 'bg-warning',
        'excluded': 'bg-danger',
        null: 'bg-secondary'
      };
      return classMap[status] || 'bg-secondary';
    },

    getCoverageIcon(status) {
      const iconMap = {
        'fully_covered': 'bx bx-check-circle me-1',
        'partially_covered': 'bx bx-adjust me-1',
        'excluded': 'bx bx-x-circle me-1',
        null: 'bx bx-question-mark me-1'
      };
      return iconMap[status] || 'bx bx-question-mark me-1';
    },

    formatCoverageStatus(status) {
      const statusMap = {
        'fully_covered': 'Fully Covered',
        'partially_covered': 'Partial',
        'excluded': 'Excluded',
        null: 'Not Set'
      };
      return statusMap[status] || 'Unknown';
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    // Alert management
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.spinner-border {
  width: 3rem;
  height: 3rem;
}

.bg-soft-primary {
  background-color: rgba(78, 115, 223, 0.1);
}

.bg-soft-secondary {
  background-color: rgba(133, 135, 150, 0.1);
}

.bg-soft-info {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-soft-dark {
  background-color: rgba(52, 58, 64, 0.1);
}

.table td {
  vertical-align: middle;
}

.pagination {
  margin-bottom: 0;
}

.form-check-input {
  cursor: pointer;
}
</style>